import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import DrawingCanvas from './DrawingCanvas';
import FishboneDiagramVisual from './FishboneDiagramVisual';
import WorkflowPanel from './WorkflowPanel';
import NotificationsPanel from './NotificationsPanel';


import GlobalNotificationCenter from './GlobalNotificationCenter';
import ThemeToggle from './ThemeToggle';
import TemplateSelector from './TemplateSelector';
import FormsHistory from './FormsHistory';
import { generatePDF, generateDetailedPDF } from '../utils/pdfGenerator';

function MainApp() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    equipement_zone_ligne: '',
    date_heure: '',
    noms: '',
    probleme_initial: '',
    phenomene_produit: '',
    phenomene_ideal: '',
    drawing_produit: '',
    drawing_ideal: '',
    centerlines_verified: false,
    centerlines_details: '',
    centerlines_target: '',
    centerlines_actuelle: '',
    clipm_completed: false,
    rls_cil_status: '',
    taches_pm_status: '',
    dh_status: '',
    conditions_commentaires: '',
    materiaux_conformes: false,
    materiaux_details: '',
    materiaux_target: '',
    materiaux_actuelle: '',
    operateurs_qualifies: false,
    standard_defini: false,
    process_connu: false,
    tache_standard_possible: false,
    standard_qualite: '',
    analyse_quand: '',
    analyse_formats: '',
    analyse_sites: '',
    analyse_frequence: '',
    probleme_reformule: '',
    probleme_corrige: false,
    check_centerlines: 'ok',
    check_centerlines_details: '',
    check_cil: 'ok',
    check_maintenance: 'ok',
    check_defaut: 'ok',
    check_visual_control: 'ok',
    fishbone_data: '',
    cause_effect_data: '',
    // Nouveaux champs v2
    statut: 'brouillon',
    priorite: 'normale',
    cout_estime: '',
    temps_resolution_prevu: '',
    assignee_user_id: '',
    tags: []
  });

  const [equipementsB3, setEquipementsB3] = useState([]);
  const [showFishbone, setShowFishbone] = useState(false);
  const [currentFormId, setCurrentFormId] = useState(null);

  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [showHistory, setShowHistory] = useState(false);


  const formRef = useRef(null);



  useEffect(() => {
    fetchEquipementsB3();
    setFormData(prev => ({
      ...prev,
      date_heure: new Date().toLocaleString('fr-FR')
    }));
    
    // Ne pas générer d'ID côté frontend - laisser le serveur le faire
    // L'ID sera défini après la première sauvegarde
  }, []);

  const fetchEquipementsB3 = async () => {
    try {
      const response = await axios.get('/api/equipements-b3');
      if (response.data.success) {
        setEquipementsB3(response.data.equipements);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des équipements:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDrawingChange = (field, drawingData) => {
    setFormData(prev => ({
      ...prev,
      [field]: drawingData
    }));
  };

  const saveForm = async () => {
    // Validation des champs obligatoires côté client
    const requiredFields = [
      { field: 'equipement_zone_ligne', label: 'Équipement / Zone / Ligne' },
      { field: 'noms', label: 'Noms' },
      { field: 'probleme_initial', label: 'Problème initial' }
    ];

    const missingFields = requiredFields.filter(req => !formData[req.field] || formData[req.field].trim() === '');

    if (missingFields.length > 0) {
      const fieldNames = missingFields.map(field => field.label).join(', ');
      alert(`⚠️ Veuillez remplir les champs obligatoires (marqués d'une *) :\n\n${fieldNames}`);
      
      // Optionnel : faire défiler vers le premier champ manquant
      const firstMissingField = document.querySelector(`[value="${formData[missingFields[0].field]}"]`);
      if (firstMissingField) {
        firstMissingField.focus();
        firstMissingField.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    console.log('🔍 Sauvegarde - currentFormId:', currentFormId);
    console.log('🔍 Sauvegarde - formData équipement:', formData.equipement_zone_ligne);
    console.log('🔍 Debug centerlines_verified:', typeof formData.centerlines_verified, formData.centerlines_verified);
    
    // 🔧 Nettoyer les types de données avant envoi (fix validation)
    const cleanedFormData = {
      ...formData,
      // S'assurer que tous les booléens sont bien des booléens
      centerlines_verified: Boolean(formData.centerlines_verified),
      clipm_completed: Boolean(formData.clipm_completed),
      materiaux_conformes: Boolean(formData.materiaux_conformes),
      operateurs_qualifies: Boolean(formData.operateurs_qualifies),
      standard_defini: Boolean(formData.standard_defini),
      process_connu: Boolean(formData.process_connu),
      tache_standard_possible: Boolean(formData.tache_standard_possible),
      probleme_corrige: Boolean(formData.probleme_corrige)
    };
    
    console.log('🔧 FormData nettoyé:', cleanedFormData);
    
    try {
      if (currentFormId && currentFormId !== null && !currentFormId.startsWith('FORM-')) {
        // 🔄 MISE À JOUR d'un formulaire existant (PUT)
        console.log('🔄 Mise à jour formulaire existant:', currentFormId);
        const response = await axios.put(`/api/formulaires/${currentFormId}`, cleanedFormData);
        console.log('✅ Formulaire mis à jour avec succès');
        alert('🔄 Formulaire mis à jour avec succès !');
      } else {
        // ➕ CRÉATION d'un nouveau formulaire (POST)
        console.log('➕ Création nouveau formulaire');
        const response = await axios.post('/api/formulaires', cleanedFormData);
        const newId = response.data.id;
        setCurrentFormId(newId);
        console.log('✅ Nouveau formulaire créé avec ID:', newId);
        alert('➕ Nouveau formulaire créé avec succès !');
      }
      
      // TODO: Implémenter la mise à jour plus tard quand le système sera stable
      
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      
      // Affichage d'erreur plus convivial
      if (error.response?.data?.details) {
        const details = Array.isArray(error.response.data.details) 
          ? error.response.data.details.join('\n') 
          : error.response.data.details;
        alert(`❌ Erreur de validation :\n\n${details}`);
      } else {
        alert('❌ Erreur lors de la sauvegarde du formulaire: ' + (error.response?.data?.error || error.message));
      }
    }
  };

  // 📂 Charger un formulaire existant depuis l'historique
  const loadFormFromHistory = async (formulaireId) => {
    try {
      console.log('📂 Chargement formulaire:', formulaireId);
      console.log('🔍 État formData AVANT chargement:', formData);
      
      // Récupérer le token d'authentification
      const token = localStorage.getItem('6w2h_token');
      if (!token) {
        throw new Error('Token d\'authentification manquant');
      }
      
                  const response = await axios.get(`http://localhost:5000/api/formulaires/${formulaireId}`, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              withCredentials: true  // Inclure les cookies d'authentification
            });
            
            // Le serveur renvoie {success: true, data: formulaire}
            const loadedFormData = response.data.success ? response.data.data : response.data;
      
                  console.log('📥 Réponse brute serveur:', response.data);
            console.log('📥 Données formulaire extraites:', loadedFormData);
            
            // 🔍 Debug des données complexes
            console.log('🎨 Debug drawing_produit:', loadedFormData.drawing_produit ? `${loadedFormData.drawing_produit.substring(0, 50)}...` : 'VIDE');
            console.log('🎨 Debug drawing_ideal:', loadedFormData.drawing_ideal ? `${loadedFormData.drawing_ideal.substring(0, 50)}...` : 'VIDE');
            console.log('🐟 Debug fishbone_data:', loadedFormData.fishbone_data ? loadedFormData.fishbone_data : 'VIDE');
            
            // 🔧 Fonction de décodage HTML avancée (gère le double échappement)
            const decodeHtmlEntities = (text) => {
              if (!text || typeof text !== 'string') return text;
              
              let decoded = text;
              let previousDecoded = '';
              
              // Décodage récursif jusqu'à ce qu'il n'y ait plus de changement
              while (decoded !== previousDecoded) {
                previousDecoded = decoded;
                decoded = decoded
                  // Entités multiples (double échappement)
                  .replace(/&amp;amp;/g, '&')
                  .replace(/&amp;quot;/g, '"')
                  .replace(/&amp;#x27;/g, "'")
                  .replace(/&amp;#x2F;/g, '/')
                  .replace(/&amp;#x3A;/g, ':')
                  .replace(/&amp;lt;/g, '<')
                  .replace(/&amp;gt;/g, '>')
                  // Entités simples
                  .replace(/&amp;/g, '&')
                  .replace(/&quot;/g, '"')
                  .replace(/&#x27;/g, "'")
                  .replace(/&#x2F;/g, '/')
                  .replace(/&#x3A;/g, ':')
                  .replace(/&lt;/g, '<')
                  .replace(/&gt;/g, '>')
                  // Entités pour caractères accentués
                  .replace(/&eacute;/g, 'é')
                  .replace(/&egrave;/g, 'è')
                  .replace(/&ecirc;/g, 'ê')
                  .replace(/&agrave;/g, 'à')
                  .replace(/&acirc;/g, 'â')
                  .replace(/&ccedil;/g, 'ç')
                  .replace(/&ocirc;/g, 'ô')
                  .replace(/&ucirc;/g, 'û')
                  .replace(/&ugrave;/g, 'ù')
                  .replace(/&icirc;/g, 'î')
                  .replace(/&iuml;/g, 'ï');
              }
              
              console.log('🔧 Décodage HTML:', text, '→', decoded);
              return decoded;
            };

            // 🎨 Décoder les données de dessin si présentes
            if (loadedFormData.drawing_produit) {
              loadedFormData.drawing_produit = decodeHtmlEntities(loadedFormData.drawing_produit);
              console.log('🎨 Décodage drawing_produit terminé');
            }
            if (loadedFormData.drawing_ideal) {
              loadedFormData.drawing_ideal = decodeHtmlEntities(loadedFormData.drawing_ideal);
              console.log('🎨 Décodage drawing_ideal terminé');
            }

            // 🐟 Décoder les données Fishbone si présentes
            if (loadedFormData.fishbone_data) {
              const decodedFishbone = decodeHtmlEntities(loadedFormData.fishbone_data);
              loadedFormData.fishbone_data = decodedFishbone;
              console.log('🐟 Décodage fishbone_data terminé:', decodedFishbone);
            }

            // 📅 Formater la date pour l'affichage dans les inputs
            if (loadedFormData.date_heure) {
              const formatDateForInput = (dateString) => {
                if (!dateString) return '';
                
                // Décoder les entités HTML
                const decodedDate = decodeHtmlEntities(dateString);
                
                // Si c'est au format ISO (2025-08-15T12:52:00.421Z), le formater en français
                if (decodedDate.includes('T') && decodedDate.includes('Z')) {
                  const date = new Date(decodedDate);
                  return date.toLocaleString('fr-FR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit'
                  });
                }
                
                return decodedDate;
              };
              
              loadedFormData.date_heure = formatDateForInput(loadedFormData.date_heure);
            }
      
      // Fusionner avec les valeurs par défaut pour éviter les undefined
      const defaultFormData = {
        // Champs obligatoires
        equipement_zone_ligne: '',
        date_heure: '',
        noms: '',
        probleme_initial: '',
        phenomene_produit: '',
        phenomene_ideal: '',
        drawing_produit: '',
        drawing_ideal: '',
        centerlines_verified: false,
        centerlines_details: '',
        centerlines_target: '',
        centerlines_actuelle: '',
        clipm_completed: false,
        rls_cil_status: '',
        taches_pm_status: '',
        dh_status: '',
        conditions_commentaires: '',
        materiaux_conformes: false,
        materiaux_details: '',
        materiaux_target: '',
        materiaux_actuelle: '',
        operateurs_qualifies: false,
        standard_defini: false,
        process_connu: false,
        tache_standard_possible: false,
        standard_qualite: '',
        analyse_quand: '',
        analyse_formats: '',
        analyse_sites: '',
        analyse_frequence: '',
        probleme_reformule: '',
        probleme_corrige: false,
        check_centerlines: 'ok',
        check_centerlines_details: '',
        check_cil: 'ok',
        check_maintenance: 'ok',
        check_defaut: 'ok',
        check_visual_control: 'ok',
        fishbone_data: '',
        cause_effect_data: '',
        // Nouveaux champs v2
        statut: 'brouillon',
        priorite: 'normale',
        cout_estime: '',
        temps_resolution_prevu: '',
        assignee_user_id: '',
        tags: []
      };

      const safeFormData = {};
      Object.keys(defaultFormData).forEach(key => {
        if (typeof defaultFormData[key] === 'string') {
          // Décoder les entités HTML pour tous les champs texte
          const value = loadedFormData[key] || '';
          safeFormData[key] = decodeHtmlEntities(value);
        } else if (typeof defaultFormData[key] === 'boolean') {
          safeFormData[key] = loadedFormData[key] !== undefined ? loadedFormData[key] : defaultFormData[key];
        } else if (Array.isArray(defaultFormData[key])) {
          safeFormData[key] = loadedFormData[key] || [];
        } else {
          safeFormData[key] = loadedFormData[key] !== undefined ? loadedFormData[key] : defaultFormData[key];
        }
      });
      
      setFormData(safeFormData);
      setCurrentFormId(loadedFormData.id);
      setShowHistory(false);
      
      console.log('🔍 safeFormData calculé:', safeFormData);
      console.log('✅ Formulaire chargé avec succès');
      
      // Vérifier l'état après setFormData avec un timeout
      setTimeout(() => {
        console.log('🔍 État formData APRÈS setFormData:', formData);
        
        // Forcer le rafraîchissement des composants complexes
        console.log('🔄 Forçage rafraîchissement composants complexes...');
        
        // Si le Fishbone est ouvert, le fermer et le rouvrir pour forcer le rafraîchissement
        if (showFishbone) {
          setShowFishbone(false);
          setTimeout(() => setShowFishbone(true), 100);
        }
        
        // Notification visuelle de mise à jour
        const notification = document.createElement('div');
        notification.innerHTML = '🔄 Composants mis à jour !';
        notification.style.cssText = `
          position: fixed; top: 20px; right: 20px; z-index: 10000;
          background: #10b981; color: white; padding: 10px 15px;
          border-radius: 8px; font-weight: bold; 
          animation: slideIn 0.3s ease-out;
        `;
        document.body.appendChild(notification);
        setTimeout(() => document.body.removeChild(notification), 2000);
      }, 100);
      
      alert('📂 Formulaire chargé avec succès !');
      
      // Scroll vers le haut du formulaire
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
    } catch (error) {
      console.error('Erreur chargement formulaire:', error);
      alert('❌ Erreur lors du chargement du formulaire: ' + (error.response?.data?.error || error.message));
    }
  };



  const exportToPDF = async () => {
    if (!formRef.current) {
      alert('Erreur: Impossible de capturer le formulaire');
      return;
    }

    try {
      const result = await generatePDF(formData, formRef.current);
      if (result.success) {
        alert(`PDF généré avec succès: ${result.filename}`);
      } else {
        alert(`Erreur lors de la génération PDF: ${result.error}`);
      }
    } catch (error) {
      console.error('Erreur lors de l\'export PDF:', error);
      alert('Erreur lors de la génération du PDF');
    }
  };

  const exportDetailedPDF = async () => {
    try {
      const result = await generateDetailedPDF(formData);
      if (result.success) {
        alert(`PDF détaillé généré avec succès: ${result.filename}`);
      } else {
        alert(`Erreur lors de la génération PDF détaillé: ${result.error}`);
      }
    } catch (error) {
      console.error('Erreur lors de l\'export PDF détaillé:', error);
      alert('Erreur lors de la génération du PDF détaillé');
    }
  };

  const printForm = () => {
    window.print();
  };

  const newForm = () => {
    // Générer un nouvel ID de formulaire
    const newFormId = `FORM-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setCurrentFormId(newFormId);
    
    setFormData({
      equipement_zone_ligne: '',
      date_heure: new Date().toLocaleString('fr-FR'),
      noms: '',
      probleme_initial: '',
      phenomene_produit: '',
      phenomene_ideal: '',
      drawing_produit: '',
      drawing_ideal: '',
      centerlines_verified: false,
      centerlines_details: '',
      centerlines_target: '',
      centerlines_actuelle: '',
      clipm_completed: false,
      rls_cil_status: '',
      taches_pm_status: '',
      dh_status: '',
      conditions_commentaires: '',
      materiaux_conformes: false,
      materiaux_details: '',
      materiaux_target: '',
      materiaux_actuelle: '',
      operateurs_qualifies: false,
      standard_defini: false,
      process_connu: false,
      tache_standard_possible: false,
      standard_qualite: '',
      analyse_quand: '',
      analyse_formats: '',
      analyse_sites: '',
      analyse_frequence: '',
      probleme_reformule: '',
      probleme_corrige: false,
      check_centerlines: 'ok',
      check_centerlines_details: '',
      check_cil: 'ok',
      check_maintenance: 'ok',
      check_defaut: 'ok',
      check_visual_control: 'ok',
      fishbone_data: '',
      cause_effect_data: '',
      statut: 'brouillon',
      priorite: 'normale',
      cout_estime: '',
      temps_resolution_prevu: '',
      assignee_user_id: '',
      tags: []
    });
    setCurrentFormId(null);
    setShowFishbone(false);
  };



  // Fonction pour appliquer un template
  const applyTemplate = (templateData) => {
    setFormData(prevData => ({
      ...prevData,
      ...templateData,
      // Préserver certains champs existants si ils sont déjà remplis
      equipement_zone_ligne: templateData.equipement_zone_ligne || prevData.equipement_zone_ligne,
      date_heure: prevData.date_heure, // Garder la date actuelle
      noms: templateData.noms || prevData.noms,
      // Garder l'ID et les métadonnées existantes
      created_at: prevData.created_at,
      updated_at: prevData.updated_at
    }));
    
    // Notification de l'application du template
    const notification = document.createElement('div');
    notification.textContent = '📋 Template appliqué avec succès !';
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: var(--success-color);
      color: white;
      padding: 12px 20px;
      border-radius: 8px;
      font-size: 14px;
      z-index: 10000;
      animation: slideInRight 0.3s ease-out;
    `;
    document.body.appendChild(notification);
    setTimeout(() => notification.remove(), 3000);
  };

  // Configuration des raccourcis clavier
  useKeyboardShortcuts({
    onSave: saveForm,
    onPrint: () => window.print(),
    onExportPDF: exportToPDF,
    onNewForm: newForm,
    onGoToDashboard: () => navigate('/dashboard'),
    onGoToApprovals: () => navigate('/approvals'),
    onToggleFishbone: () => setShowFishbone(!showFishbone),
    onEscape: () => {
      setShowFishbone(false);
      setShowTemplateSelector(false);
    },
    onOpenTemplates: () => setShowTemplateSelector(true)
  });

  return (
    <div className="App">
      {/* Barre de navigation v2 */}
      <div style={{
        background: '#9cb85c',
        padding: '10px 20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '2px solid #000'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <h1 style={{ margin: 0, color: 'white', fontSize: '24px' }}>
            📋 Formulaire 6W2H v2.0 
            {currentFormId && !currentFormId.startsWith('FORM-') && (
              <span style={{ fontSize: '16px', marginLeft: '10px', opacity: 0.8 }}>
                (Édition #{currentFormId.slice(-8)})
              </span>
            )}
          </h1>
          <div style={{ background: 'rgba(255,255,255,0.2)', padding: '5px 10px', borderRadius: '15px', fontSize: '12px', color: 'white' }}>
            {formData.statut} - {formData.priorite}
          </div>
          
          {/* Boutons navigation */}
          {['superviseur', 'manager'].includes(user?.role) && (
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => navigate('/dashboard')}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  border: '1px solid rgba(255,255,255,0.3)',
                  padding: '6px 12px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                📊 Dashboard
              </button>
              <button
                onClick={() => navigate('/approvals')}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  border: '1px solid rgba(255,255,255,0.3)',
                  padding: '6px 12px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                ✅ Approbations
              </button>
            </div>
          )}
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <NotificationsPanel />
          
          <button 
            onClick={() => setShowHistory(true)}
            style={{
              background: 'none',
              border: '1px solid rgba(255,255,255,0.3)',
              color: 'white',
              padding: '8px 12px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = 'rgba(255,255,255,0.2)';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = 'transparent';
            }}
            title="Voir mon historique de formulaires"
          >
            📋 Mon Historique
          </button>
          





          <ThemeToggle className="nav-theme-toggle" />
          <span style={{ color: 'white', fontSize: '14px' }}>
            👤 Bonjour {user?.prenom} {user?.nom} ({user?.role})
          </span>
          <button 
            onClick={logout}
            style={{
              background: 'rgba(255,255,255,0.2)',
              color: 'white',
              border: '1px solid rgba(255,255,255,0.3)',
              padding: '8px 15px',
              borderRadius: '5px',
              cursor: 'pointer'
            }}
          >
            Déconnexion
          </button>
        </div>
      </div>

      <div className="form-6w2h" ref={formRef}>
        {/* Message d'information pour les champs obligatoires */}
        <div className="required-fields-info">
          <strong>📝 Information :</strong> Les champs marqués d'une <span className="asterisk">*</span> sont obligatoires pour sauvegarder le formulaire.
        </div>

        {/* En-tête */}
        <div className="header-section">
          <div className="header-title">6W2H</div>
          <div className="header-fields">
            <div className="field-group">
              <label className="field-label">Équipement / Zone / Ligne <span style={{color: 'red', fontSize: '16px'}}>*</span> :</label>
              <select
                className={`field-input required ${formData.equipement_zone_ligne ? 'valid' : ''}`}
                value={formData.equipement_zone_ligne}
                onChange={(e) => handleInputChange('equipement_zone_ligne', e.target.value)}
              >
                <option value="">Sélectionner un équipement...</option>
                {equipementsB3.map((eq, index) => (
                  <option key={index} value={`Ligne B3 - ${eq.nom_equipement}`}>
                    Ligne B3 - {eq.nom_equipement} ({eq.transformations})
                  </option>
                ))}
              </select>
            </div>
            <div className="field-group">
              <label className="field-label">Date et heure :</label>
              <input
                type="text"
                className="field-input"
                value={formData.date_heure}
                onChange={(e) => handleInputChange('date_heure', e.target.value)}
              />
            </div>
            <div className="field-group">
              <label className="field-label">Noms <span style={{color: 'red', fontSize: '16px'}}>*</span> :</label>
              <input
                type="text"
                className={`field-input required ${formData.noms ? 'valid' : ''}`}
                value={formData.noms}
                onChange={(e) => handleInputChange('noms', e.target.value)}
                placeholder="Entrez les noms des personnes impliquées"
              />
            </div>
            <div className="field-group problem-initial">
              <label className="field-label">Problème initial <span style={{color: 'red', fontSize: '16px'}}>*</span> :</label>
              <input
                type="text"
                className={`field-input required ${formData.probleme_initial ? 'valid' : ''}`}
                value={formData.probleme_initial}
                onChange={(e) => handleInputChange('probleme_initial', e.target.value)}
                placeholder="Décrivez le problème initial observé"
              />
            </div>
          </div>
        </div>

        {/* Nouveaux champs v2 - Statut et priorité */}
        <div style={{ 
          padding: '15px', 
          background: '#f8f9fa', 
          borderBottom: '1px solid #dee2e6',
          display: 'flex',
          gap: '20px',
          alignItems: 'center'
        }}>
          <div>
            <label style={{ fontWeight: 'bold', marginRight: '10px' }}>Statut :</label>
            <select
              value={formData.statut}
              onChange={(e) => handleInputChange('statut', e.target.value)}
              style={{ padding: '5px', borderRadius: '3px', border: '1px solid #ccc' }}
            >
              <option value="brouillon">Brouillon</option>
              <option value="en_cours">En cours</option>
              <option value="valide">Validé</option>
              <option value="rejete">Rejeté</option>
              <option value="archive">Archivé</option>
            </select>
          </div>
          
          <div>
            <label style={{ fontWeight: 'bold', marginRight: '10px' }}>Priorité :</label>
            <select
              value={formData.priorite}
              onChange={(e) => handleInputChange('priorite', e.target.value)}
              style={{ padding: '5px', borderRadius: '3px', border: '1px solid #ccc' }}
            >
              <option value="faible">Faible</option>
              <option value="normale">Normale</option>
              <option value="haute">Haute</option>
              <option value="critique">Critique</option>
            </select>
          </div>

          <div>
            <label style={{ fontWeight: 'bold', marginRight: '10px' }}>Coût estimé (€) :</label>
            <input
              type="number"
              value={formData.cout_estime}
              onChange={(e) => handleInputChange('cout_estime', e.target.value)}
              style={{ padding: '5px', borderRadius: '3px', border: '1px solid #ccc', width: '120px' }}
              placeholder="25000"
            />
          </div>
        </div>

        {/* Sections principales - Phénomènes */}
        <div className="main-content">
          <div className="phenomenon-section">
            <div className="section-title">
              Dessinez et décrivez le phénomène qui se produit
            </div>
            <div className="section-description">
              Allez sur le terrain, observez le phénomène et/ou voyez le problème et dessinez. 
              Ne prenez pas l'initiative de corriger pour mieux comprendre le problème. 
              Dessinez jusqu'à ce que vous compreniez le problème et qu'il soit le plus clair possible.
              <br /><br />
              <strong>QUOI</strong> – Décrivez le phénomène qui se produit sur le poste/problème/étape 
              par rapport aux composants/de la machine.
              <br />
              <strong>COMMENT</strong> – Décrivez comment le phénomène se produit.
              <br />
              <strong>OÙ</strong> – Décrivez où se situe le problème.
            </div>
            <div className="drawing-area">
              <DrawingCanvas
                key={`drawing_produit_${currentFormId || 'new'}`}
                onDrawingChange={(data) => handleDrawingChange('drawing_produit', data)}
                initialData={formData.drawing_produit}
              />
            </div>
            <textarea
              className="text-area"
              placeholder="Décrivez le phénomène observé..."
              value={formData.phenomene_produit}
              onChange={(e) => handleInputChange('phenomene_produit', e.target.value)}
            />
          </div>

          <div className="phenomenon-ideal">
            <div className="section-title">
              Dessinez et décrivez le phénomène qui devrait se produire (état idéal)
            </div>
            <div className="section-description">
              Référez-vous aux standards existants, MTM et WPA, et aux plans de l'équipement 
              s'ils sont disponibles.
            </div>
            <div className="drawing-area">
              <DrawingCanvas
                key={`drawing_ideal_${currentFormId || 'new'}`}
                onDrawingChange={(data) => handleDrawingChange('drawing_ideal', data)}
                initialData={formData.drawing_ideal}
              />
            </div>
            <textarea
              className="text-area"
              placeholder="Décrivez l'état idéal..."
              value={formData.phenomene_ideal}
              onChange={(e) => handleInputChange('phenomene_ideal', e.target.value)}
            />
          </div>
        </div>

        {/* Bande d'instructions */}
        <div className="instruction-band">
          Commencez le check des conditions de base lorsque vous avez <strong>limité la zone de la perte</strong> à une zone spécifique (workpoint).
        </div>

        {/* Tables de vérification */}
        <div className="verification-section">
          {/* Tableau 1: Vérifiez les réglages de l'équipement */}
          <table className="verification-table">
            <thead>
              <tr>
                <th>Vérifiez les réglages de l'équipement</th>
                <th>Vérifiez les conditions de l'équipement</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <div className="checkbox-group">
                    <span>Les centerlines ont été vérifiés et sont au target ?</span>
                    <label>
                      <input
                        type="radio"
                        name="centerlines_verified"
                        checked={formData.centerlines_verified === true}
                        onChange={() => handleInputChange('centerlines_verified', true)}
                      />
                      Oui
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="centerlines_verified"
                        checked={formData.centerlines_verified === false}
                        onChange={() => handleInputChange('centerlines_verified', false)}
                      />
                      Non
                    </label>
                  </div>
                  <div style={{ marginTop: '10px' }}>
                    <span style={{ fontSize: '10px' }}>Liste ci-dessous les centerlines hors limite (Ex. WPA) :</span>
                    <br />
                    <span style={{ fontSize: '9px' }}>Longueur Target de colle 205 (± 50 mm versus 30 mm NOK)</span>
                    <textarea
                      className="small-input"
                      style={{ height: '40px', marginTop: '5px', width: '100%', border: '1px solid #000', padding: '2px' }}
                      value={formData.centerlines_details}
                      onChange={(e) => handleInputChange('centerlines_details', e.target.value)}
                    />
                  </div>
                </td>
                <td>
                  <div className="checkbox-group" style={{ marginBottom: '10px' }}>
                    <span>Les CLIPM liés à ce workpoint sont complétés ?</span>
                    <label>
                      <input
                        type="radio"
                        name="clipm_completed"
                        checked={formData.clipm_completed === true}
                        onChange={() => handleInputChange('clipm_completed', true)}
                      />
                      Oui
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="clipm_completed"
                        checked={formData.clipm_completed === false}
                        onChange={() => handleInputChange('clipm_completed', false)}
                      />
                      Non
                    </label>
                  </div>
                  <div style={{ fontSize: '10px', marginBottom: '8px' }}>
                    <strong>RLS / CIL</strong> (cochez dans CIL) (vérification visuelle : absence de surcontamination)
                  </div>
                  <div style={{ fontSize: '10px', marginBottom: '8px' }}>
                    <strong>Tâches PM</strong> (toutes les PM réalisées à temps)
                  </div>
                  <div style={{ fontSize: '10px', marginBottom: '8px' }}>
                    <strong>DH</strong> (pas de défaut ouvert dans e-DH / tour défaut réalisé)
                  </div>
                  <div style={{ fontSize: '10px' }}>
                    <strong>Commentaires</strong> : description des tâches CLIPM non réalisées, défauts trouvés.
                    <textarea
                      className="small-input"
                      style={{ height: '40px', marginTop: '5px', width: '100%', border: '1px solid #000', padding: '2px' }}
                      value={formData.conditions_commentaires}
                      onChange={(e) => handleInputChange('conditions_commentaires', e.target.value)}
                    />
                  </div>
                </td>
              </tr>
              <tr>
                <td>
                  <div style={{ display: 'flex', gap: '20px', marginTop: '10px' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '10px', fontWeight: 'bold', marginBottom: '5px' }}>Target</div>
                      <input
                        type="text"
                        style={{ width: '100%', border: '1px solid #000', padding: '2px' }}
                        value={formData.centerlines_target}
                        onChange={(e) => handleInputChange('centerlines_target', e.target.value)}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '10px', fontWeight: 'bold', marginBottom: '5px' }}>Actuelle</div>
                      <input
                        type="text"
                        style={{ width: '100%', border: '1px solid #000', padding: '2px' }}
                        value={formData.centerlines_actuelle}
                        onChange={(e) => handleInputChange('centerlines_actuelle', e.target.value)}
                      />
                    </div>
                  </div>
                </td>
                <td></td>
              </tr>
            </tbody>
          </table>

          {/* Tableau 2: Vérifiez les conditions des matériaux */}
          <table className="verification-table">
            <thead>
              <tr>
                <th>Vérifiez les conditions des matériaux</th>
                <th>Vérifiez les conditions de l'opération</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <div className="checkbox-group">
                    <span>Les matériaux sont en ligne avec les spécifications (Enovia) ?</span>
                    <label>
                      <input
                        type="radio"
                        name="materiaux_conformes"
                        checked={formData.materiaux_conformes === true}
                        onChange={() => handleInputChange('materiaux_conformes', true)}
                      />
                      Oui
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="materiaux_conformes"
                        checked={formData.materiaux_conformes === false}
                        onChange={() => handleInputChange('materiaux_conformes', false)}
                      />
                      Non
                    </label>
                  </div>
                  <div style={{ marginTop: '10px', fontSize: '10px' }}>
                    Listez les spécifications hors target et valeur actuelle – retenez des échantillons des matériaux hors spec
                    <textarea
                      className="small-input"
                      style={{ height: '40px', marginTop: '5px', width: '100%', border: '1px solid #000', padding: '2px' }}
                      value={formData.materiaux_details}
                      onChange={(e) => handleInputChange('materiaux_details', e.target.value)}
                    />
                  </div>
                </td>
                <td>
                  <div style={{ fontSize: '10px', marginBottom: '8px' }}>
                    <div className="checkbox-group" style={{ marginBottom: '5px' }}>
                      <span>Les opérateurs (intérimaires, nouveaux embauchés...) sont qualifiés pour faire la tâche/activité et ils exécutent la tâche selon le standard ?</span>
                      <label>
                        <input
                          type="radio"
                          name="operateurs_qualifies"
                          checked={formData.operateurs_qualifies === true}
                          onChange={() => handleInputChange('operateurs_qualifies', true)}
                        />
                        Oui
                      </label>
                      <label>
                        <input
                          type="radio"
                          name="operateurs_qualifies"
                          checked={formData.operateurs_qualifies === false}
                          onChange={() => handleInputChange('operateurs_qualifies', false)}
                        />
                        Non
                      </label>
                    </div>
                    <div style={{ marginBottom: '5px' }}>
                      Est-ce qu'il y a un standard défini et documenté ? (Vérifier dans Veeva les SOP et Job Aid)
                    </div>
                    <div style={{ marginBottom: '5px' }}>
                      Est-ce que le process est connu ? (Discuter avec les personnes concernées sur le process)
                    </div>
                    <div style={{ marginBottom: '5px' }}>
                      Est-ce qu'il est possible de relier la tâche avec le standard ? (Vérifier le standard et comparer avec les étapes et séquences attendues)
                    </div>
                    <div>
                      Sinon, évaluer sa qualité dans quel domaine ? Quel standard doit être mis en place ?
                    </div>
                  </div>
                </td>
              </tr>
              <tr>
                <td>
                  <div style={{ display: 'flex', gap: '20px' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '10px', fontWeight: 'bold', marginBottom: '5px' }}>Target</div>
                      <input
                        type="text"
                        style={{ width: '100%', border: '1px solid #000', padding: '2px' }}
                        value={formData.materiaux_target}
                        onChange={(e) => handleInputChange('materiaux_target', e.target.value)}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '10px', fontWeight: 'bold', marginBottom: '5px' }}>Actuelle</div>
                      <input
                        type="text"
                        style={{ width: '100%', border: '1px solid #000', padding: '2px' }}
                        value={formData.materiaux_actuelle}
                        onChange={(e) => handleInputChange('materiaux_actuelle', e.target.value)}
                      />
                    </div>
                  </div>
                </td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Bande verte bas tableau */}
        <div className="instruction-band">
          Si la perte est éliminée par le rétablissement des conditions de base – passer à la root cause (étape 7), sinon continuer à analyser le problème (étape 5).
        </div>

        {/* Section Analyse */}
        <div className="analysis-section">
          <div className="analysis-grid">
            <div>
              <div className="analysis-field">
                <label className="analysis-label">Quand le problème est-il apparu ?</label>
                <div style={{ fontSize: '10px', marginBottom: '5px' }}>
                  Démarrage, C/O, accélération/décélération suite à un stop, introduction d'un nouveau format.
                </div>
                <textarea
                  className="analysis-textarea"
                  value={formData.analyse_quand}
                  onChange={(e) => handleInputChange('analyse_quand', e.target.value)}
                />
              </div>

              <div className="analysis-field">
                <label className="analysis-label">Quels formats sont impactés ?</label>
                <div style={{ fontSize: '10px', marginBottom: '5px' }}>
                  GCAV / IPMS / format / case count / type de palettisation.
                </div>
                <textarea
                  className="analysis-textarea"
                  value={formData.analyse_formats}
                  onChange={(e) => handleInputChange('analyse_formats', e.target.value)}
                />
              </div>
            </div>

            <div>
              <div className="analysis-field">
                <label className="analysis-label">Quels sites, systèmes, départements, équipes ont déjà rencontré ce problème ? Lesquels et en quoi sont-ils concernés ?</label>
                <textarea
                  className="analysis-textarea"
                  value={formData.analyse_sites}
                  onChange={(e) => handleInputChange('analyse_sites', e.target.value)}
                />
              </div>

              <div className="analysis-field">
                <label className="analysis-label">À quelle fréquence cette perte apparaît-elle ?</label>
                <div style={{ fontSize: '10px', marginBottom: '5px' }}>
                  Quantifiez la perte par rapport à MUL, Stops, Effort.
                </div>
                <textarea
                  className="analysis-textarea"
                  value={formData.analyse_frequence}
                  onChange={(e) => handleInputChange('analyse_frequence', e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Section Problème reformulé */}
        <div className="reformulated-section">
          <label className="analysis-label">Problème reformulé</label>
          <div className="reformulated-description">
            Utiliser toutes les informations des étapes 6W2H. Le problème reformulé est plus spécifique et clair que le problème initial. Soyez factuel, ne faites pas de supposition.
          </div>
          <textarea
            className="analysis-textarea"
            style={{ minHeight: '80px' }}
            value={formData.probleme_reformule}
            onChange={(e) => handleInputChange('probleme_reformule', e.target.value)}
          />
        </div>



        {/* Vérifications spécifiques B3 */}
        <div className="b3-verification-section">
          <div className="b3-verification-title">
            Vérifications spécifiques Ligne B3 (v2.0)
          </div>
          <div className="b3-checks">
            <div className="b3-check-item">
              <span>Check Centerlines :</span>
              <label>
                <input
                  type="radio"
                  name="check_centerlines"
                  value="ok"
                  checked={formData.check_centerlines === 'ok'}
                  onChange={(e) => handleInputChange('check_centerlines', e.target.value)}
                />
                Ok
              </label>
              <label>
                <input
                  type="radio"
                  name="check_centerlines"
                  value="nok"
                  checked={formData.check_centerlines === 'nok'}
                  onChange={(e) => handleInputChange('check_centerlines', e.target.value)}
                />
                Nok
              </label>
            </div>

            <div className="b3-check-item">
              <span>Check CIL :</span>
              <label>
                <input
                  type="radio"
                  name="check_cil"
                  value="ok"
                  checked={formData.check_cil === 'ok'}
                  onChange={(e) => handleInputChange('check_cil', e.target.value)}
                />
                Ok
              </label>
              <label>
                <input
                  type="radio"
                  name="check_cil"
                  value="nok"
                  checked={formData.check_cil === 'nok'}
                  onChange={(e) => handleInputChange('check_cil', e.target.value)}
                />
                Nok
              </label>
            </div>

            <div className="b3-check-item">
              <span>Check maintenance :</span>
              <label>
                <input
                  type="radio"
                  name="check_maintenance"
                  value="ok"
                  checked={formData.check_maintenance === 'ok'}
                  onChange={(e) => handleInputChange('check_maintenance', e.target.value)}
                />
                Ok
              </label>
              <label>
                <input
                  type="radio"
                  name="check_maintenance"
                  value="nok"
                  checked={formData.check_maintenance === 'nok'}
                  onChange={(e) => handleInputChange('check_maintenance', e.target.value)}
                />
                Nok
              </label>
            </div>

            <div className="b3-check-item">
              <span>Check défaut :</span>
              <label>
                <input
                  type="radio"
                  name="check_defaut"
                  value="ok"
                  checked={formData.check_defaut === 'ok'}
                  onChange={(e) => handleInputChange('check_defaut', e.target.value)}
                />
                Ok
              </label>
              <label>
                <input
                  type="radio"
                  name="check_defaut"
                  value="nok"
                  checked={formData.check_defaut === 'nok'}
                  onChange={(e) => handleInputChange('check_defaut', e.target.value)}
                />
                Nok
              </label>
            </div>

            <div className="b3-check-item">
              <span>Check Visuals control :</span>
              <label>
                <input
                  type="radio"
                  name="check_visual_control"
                  value="ok"
                  checked={formData.check_visual_control === 'ok'}
                  onChange={(e) => handleInputChange('check_visual_control', e.target.value)}
                />
                Ok
              </label>
              <label>
                <input
                  type="radio"
                  name="check_visual_control"
                  value="nok"
                  checked={formData.check_visual_control === 'nok'}
                  onChange={(e) => handleInputChange('check_visual_control', e.target.value)}
                />
                Nok
              </label>
            </div>
          </div>

          {(formData.check_centerlines === 'nok' || 
            formData.check_cil === 'nok' || 
            formData.check_maintenance === 'nok' || 
            formData.check_defaut === 'nok' || 
            formData.check_visual_control === 'nok') && (
            <div className="b3-check-details">
              <strong>Actions à entreprendre :</strong>
              <br />
              {formData.check_centerlines === 'nok' && '• Si Centerlines Nok → Champ pour Centerline\n'}
              {formData.check_cil === 'nok' && '• Si CIL Nok → Faire le CIL\n'}
              {formData.check_maintenance === 'nok' && '• Si Maintenance Nok → Demander aide au PT méca\n'}
              {formData.check_defaut === 'nok' && '• Si Défaut Nok → Demander aide au PT méca\n'}
              {formData.check_visual_control === 'nok' && '• Si Visuals control Nok → Faire les VC\n'}
              <textarea
                className="small-input"
                style={{ height: '60px', marginTop: '10px', width: '100%', border: '1px solid #000', padding: '5px' }}
                placeholder="Détails des actions entreprises..."
                value={formData.check_centerlines_details}
                onChange={(e) => handleInputChange('check_centerlines_details', e.target.value)}
              />
            </div>
          )}
        </div>

        {/* Section finale - Le problème est-il corrigé ? */}
        <div className="final-section">
          <div className="problem-resolution">
            <div className="resolution-title">
              Le problème est-il corrigé ?
            </div>
            <div className="resolution-options">
              <div className="resolution-option">
                <input
                  type="radio"
                  name="probleme_corrige"
                  checked={formData.probleme_corrige === false}
                  onChange={() => handleInputChange('probleme_corrige', false)}
                />
                <span style={{ background: '#ff6b6b', color: 'white', padding: '5px 10px', fontWeight: 'bold' }}>NON</span>
              </div>
              <div className="resolution-option">
                <input
                  type="radio"
                  name="probleme_corrige"
                  checked={formData.probleme_corrige === true}
                  onChange={() => handleInputChange('probleme_corrige', true)}
                />
                <span style={{ background: '#4caf50', color: 'white', padding: '5px 10px', fontWeight: 'bold' }}>OUI</span>
              </div>
            </div>
            <div className="resolution-details">
              <div style={{ marginBottom: '10px' }}>
                <strong>Continuer le LIR</strong><br />
                Found GPS, WPA
              </div>
              <div>
                <strong>Continuer avec le Why Why Analysis</strong>
              </div>
            </div>
          </div>

          <div className="final-checklist">
            <table className="checklist-table">
              <thead>
                <tr>
                  <th>Actions</th>
                  <th>CIL</th>
                  <th>Oui</th>
                  <th>Non</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>CLIPM</td>
                  <td>CIL</td>
                  <td>Oui</td>
                  <td>Non</td>
                </tr>
                <tr>
                  <td>Material spec.</td>
                  <td>Oui</td>
                  <td>Non</td>
                  <td></td>
                </tr>
                <tr>
                  <td>Procédures</td>
                  <td>Oui</td>
                  <td>Non</td>
                  <td></td>
                </tr>
                <tr>
                  <td>Nouveau Visual Controls</td>
                  <td>Oui</td>
                  <td>Non</td>
                  <td></td>
                </tr>
                <tr>
                  <td>PM Plan.</td>
                  <td>Oui</td>
                  <td>Non</td>
                  <td></td>
                </tr>
                <tr>
                  <td>Training</td>
                  <td>Oui</td>
                  <td>Non</td>
                  <td></td>
                </tr>
                <tr>
                  <td>eLearning</td>
                  <td>Oui</td>
                  <td>Non</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Section Diagramme Fishbone - Affiché seulement si problème NON corrigé */}
        {formData.probleme_corrige === false && (
          <div className="fishbone-section">
            <div className="fishbone-toggle">
              <button 
                className="btn btn-secondary"
                onClick={() => setShowFishbone(!showFishbone)}
                style={{ 
                  marginBottom: '10px',
                  background: showFishbone ? '#28a745' : '#6c757d'
                }}
              >
                {showFishbone ? '🐟 Masquer Fishbone' : '🐟 Afficher Diagramme Fishbone'}
              </button>
            </div>
            
            {showFishbone && (
              <FishboneDiagramVisual
                key={`fishbone_${currentFormId || 'new'}`}
                isVisible={showFishbone}
                initialData={(() => {
                  try {
                    return formData.fishbone_data ? JSON.parse(formData.fishbone_data) : null;
                  } catch (e) {
                    return null;
                  }
                })()}
                onDataChange={(data) => {
                  handleInputChange('fishbone_data', JSON.stringify(data));
                }}
              />
            )}
          </div>
        )}

        {/* Panel Workflow v2 - Temporairement désactivé */}
        {/* {currentFormId && (
          <WorkflowPanel 
            formulaireId={currentFormId}
            currentStatus={formData.statut}
            onStatusChange={(newStatus) => {
              setFormData(prev => ({ ...prev, statut: newStatus }));
            }}
          />
        )} */}



        {/* Informations Ligne B3 */}
        <div className="b3-verification-section">
          <div className="b3-verification-title">
            Informations Ligne B3 - Coût de revient : 10 000€/heure (166,666 €/minute)
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', fontSize: '10px' }}>
            {equipementsB3.map((equipement, index) => (
              <div key={index} style={{ padding: '8px', border: '1px solid #ccc', background: 'white' }}>
                <strong>{equipement.nom_equipement}</strong> (Owner: {equipement.owner})
                <br />
                <span style={{ fontSize: '9px' }}>{equipement.transformations}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Boutons d'action */}
      <div className="action-buttons">
        <button className="btn btn-primary" onClick={saveForm}>
          {currentFormId && !currentFormId.startsWith('FORM-') ? '🔄 Mettre à jour' : '💾 Sauvegarder'}
        </button>
        <button 
          className="btn btn-template" 
          onClick={() => setShowTemplateSelector(true)}
          title="Utiliser un template prédéfini (Ctrl+T)"
        >
          📋 Templates
        </button>
        <button className="btn btn-secondary" onClick={exportToPDF}>
          📄 Export PDF Visuel
        </button>
        <button className="btn btn-secondary" onClick={exportDetailedPDF}>
          📋 Export PDF Détaillé
        </button>
        <button className="btn btn-secondary" onClick={printForm}>
          🖨️ Imprimer
        </button>
        <button className="btn btn-secondary" onClick={newForm}>
          📝 Nouveau formulaire
        </button>
      </div>

      {/* Sélecteur de Templates */}
      <TemplateSelector
        isOpen={showTemplateSelector}
        onClose={() => setShowTemplateSelector(false)}
        onApplyTemplate={applyTemplate}
        currentEquipment={formData.equipement_zone_ligne}
      />





      {/* Historique des Formulaires */}
      {showHistory && (
        <FormsHistory
          onClose={() => setShowHistory(false)}
          onLoadForm={loadFormFromHistory}
        />
      )}

      {/* Centre de Notifications Globales */}
      <GlobalNotificationCenter />
    </div>
  );
}

export default MainApp;
